<template>
  <nav class="app-nav">
    <!-- 桌面端：侧边栏导航 -->
    <div class="nav-sidebar">
      <div class="nav-header">
        <h1 class="nav-title">SnapClip</h1>
        <p class="nav-subtitle">极简内容暂存</p>
      </div>

      <div class="nav-links">
        <router-link to="/" class="nav-link" active-class="active">
          <span class="nav-icon">📊</span>
          <span class="nav-text">仪表盘</span>
        </router-link>
        <router-link to="/focus" class="nav-link" active-class="active">
          <span class="nav-icon">🎯</span>
          <span class="nav-text">今日专注</span>
        </router-link>
        <router-link to="/pending" class="nav-link" active-class="active">
          <span class="nav-icon">📝</span>
          <span class="nav-text">待处理</span>
          <span v-if="pendingCount > 0" class="nav-badge">{{ pendingCount }}</span>
        </router-link>
        <router-link to="/completed" class="nav-link" active-class="active">
          <span class="nav-icon">✓</span>
          <span class="nav-text">已完成</span>
        </router-link>
        <router-link to="/archived" class="nav-link" active-class="active">
          <span class="nav-icon">📦</span>
          <span class="nav-text">归档</span>
        </router-link>
        <router-link to="/tags" class="nav-link" active-class="active">
          <span class="nav-icon">🏷️</span>
          <span class="nav-text">标签</span>
        </router-link>
      </div>

      <div class="nav-footer">
        <div class="nav-footer-row">
          <ThemeToggle />
          <ColorThemePicker />
          <ExportButton :items="items" />
        </div>
        <div v-if="isAuthenticated" class="nav-user-section">
          <button class="user-button" @click.stop="toggleUserMenu" :class="{ active: showUserMenu }">
            <div class="user-avatar">
              <img v-if="userAvatar" :src="userAvatar" alt="Avatar" />
              <span v-else class="avatar-placeholder">{{ userNickname.charAt(0).toUpperCase() }}</span>
            </div>
            <span class="user-name">{{ userNickname }}</span>
          </button>

          <Transition name="dropdown">
            <div v-if="showUserMenu" class="user-dropdown">
              <div class="user-dropdown-header">
                <div class="user-dropdown-avatar">
                  <img v-if="userAvatar" :src="userAvatar" alt="Avatar" />
                  <span v-else class="avatar-placeholder">{{ userNickname.charAt(0).toUpperCase() }}</span>
                </div>
                <div class="user-dropdown-info">
                  <div class="user-dropdown-name">{{ userNickname }}</div>
                  <div class="user-dropdown-email">{{ userEmail }}</div>
                </div>
              </div>

              <div class="user-dropdown-actions">
                <button class="dropdown-item" @click="openSettingsModal">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  个人设置
                </button>
                <button class="dropdown-item dropdown-item-danger" @click="handleLogout">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  退出登录
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useItemsStore } from '../stores/items'
import { useUserStore } from '../stores/user'
import ThemeToggle from './ThemeToggle.vue'
import ColorThemePicker from './ColorThemePicker.vue'
import ExportButton from './ExportButton.vue'

const itemsStore = useItemsStore()
const { items, pendingCount } = storeToRefs(itemsStore)

const userStore = useUserStore()
const { isAuthenticated, userEmail, userNickname, userAvatar } = storeToRefs(userStore)

const showUserMenu = ref(false)

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function openSettingsModal() {
  showUserMenu.value = false
  // 触发设置模态框
  window.dispatchEvent(new CustomEvent('open-user-settings'))
}

function handleLogout() {
  showUserMenu.value = false
  userStore.logout()
}

// 点击外部关闭下拉菜单
function handleClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('.nav-user-section')) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, { capture: false })
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style>
/* Dark mode styles - global scope to work with .dark class */
.dark .user-dropdown {
  background: rgba(31, 41, 55, 0.85) !important;
  backdrop-filter: blur(20px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05) inset !important;
}

.dark .user-dropdown-header {
  border-bottom-color: var(--border-color) !important;
}

.dark .user-dropdown-name {
  color: var(--text-primary) !important;
}

.dark .user-dropdown-email {
  color: var(--text-secondary) !important;
}

.dark .nav-link:hover {
  background: var(--bg-tertiary) !important;
}

.dark .dropdown-item {
  color: var(--text-primary) !important;
}

.dark .dropdown-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

.dark .dropdown-item-danger:hover {
  background: rgba(239, 68, 68, 0.2) !important;
}
</style>

<style scoped>
.app-nav {
  display: contents;
}

/* 桌面端侧边栏 */
.nav-sidebar {
  width: 240px;
  height: 100vh;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
}

.nav-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.nav-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

.nav-links {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
}

.nav-link:hover {
  background: var(--bg-tertiary);
}

.nav-link.active {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  color: white;
}

.nav-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.nav-text {
  flex: 1;
  font-weight: 500;
}

.nav-badge {
  background: rgba(239, 68, 68, 0.9);
  color: white;
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
}

.nav-link.active .nav-badge {
  background: rgba(255, 255, 255, 0.3);
}

.nav-footer {
  padding: 0.75rem 1rem 1rem 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  z-index: 10;
}

.nav-footer-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.nav-user-section {
  width: 100%;
  position: relative;
}

/* 统一圆形按钮样式 - 使用 !important 确保覆盖组件内的样式 */
.nav-footer-row :deep(.theme-toggle),
.nav-footer-row :deep(.picker-button),
.nav-footer-row :deep(.export-button) {
  width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: var(--bg-tertiary) !important;
  border: 1px solid var(--border-color) !important;
  border-radius: 50% !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
  color: var(--text-secondary) !important;
}

/* 悬停状态 */
.nav-footer-row :deep(.theme-toggle:hover),
.nav-footer-row :deep(.picker-button:hover),
.nav-footer-row :deep(.export-button:hover) {
  background: var(--bg-hover) !important;
  border-color: var(--color-primary) !important;
  transform: scale(1.05) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

/* 暗色模式下的悬停效果 */
:global(.dark) .nav-footer-row :deep(.theme-toggle:hover),
:global(.dark) .nav-footer-row :deep(.picker-button:hover),
:global(.dark) .nav-footer-row :deep(.export-button:hover) {
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1) !important;
}

/* 图标大小统一 */
.nav-footer-row :deep(.theme-toggle svg),
.nav-footer-row :deep(.picker-button svg) {
  width: 18px !important;
  height: 18px !important;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* 统一的旋转动画 */
.nav-footer-row :deep(.theme-toggle:hover svg) {
  transform: rotate(20deg) !important;
}

.nav-footer-row :deep(.picker-button:hover svg) {
  transform: rotate(-15deg) !important;
}

.nav-footer-row :deep(.export-button:hover svg) {
  transform: rotate(15deg) !important;
}

/* 移除 ExportButton 的文字 */
.nav-footer-row :deep(.export-button .button-text) {
  display: none !important;
}

.nav-user-section {
  flex-shrink: 0;
}

.user-info {
  position: relative;
}

.user-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-button:hover {
  background: var(--bg-hover);
}

.user-button.active {
  background: var(--bg-hover);
  border-color: var(--color-primary);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-primary);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.user-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  overflow: hidden;
  z-index: 100;
}

/* 暗色模式下的毛玻璃效果 */
:global(.dark) .user-dropdown {
  background: rgba(31, 41, 55, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

.user-dropdown-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-dropdown-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-primary);
}

.user-dropdown-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-dropdown-info {
  flex: 1;
  overflow: hidden;
}

.user-dropdown-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-email {
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-actions {
  padding: 0.5rem;
}

.dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: none;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.dropdown-item:hover {
  background: var(--bg-tertiary);
}

.dropdown-item svg {
  flex-shrink: 0;
}

.dropdown-item-danger {
  color: #ef4444;
}

.dropdown-item-danger:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* Dropdown transition - 从下往上展开 */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .nav-sidebar {
    display: none;
  }
}

/* 浮窗按钮动画 */
.nav-footer {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
